import { AxiosResponse } from 'axios';
import { APP_FEATURES } from '../../constants';
import { hasPrivilege } from '../../utility/hasPrivilege';
import { parentHomeworkService } from '../parentHomework';
import { studentHomeworkService } from '../studentHomework';
import {
  IParentHomeworkDetailResponse,
  IParentHomeworkFiltersResponse,
  IParentHomeworkListQueryParams,
  IParentHomeworkListResponse,
  IParentHomeworkSubmitResponse,
  ParentSubmissionFilesByTask,
} from '../../interface/parentHomework.interface';

/**
 * CVA-4730 — the single place that decides WHICH homework API the shared
 * homework pages talk to.
 *
 * `pages/parent/homework/` is one flow serving two audiences (the same
 * arrangement `pages/studentPortal/exam/` already uses). A child's requests
 * must go to `v2/student/homework/...` and a guardian's to
 * `v2/parent/homework/...`; the two contracts are field-for-field identical
 * and both take `studentId`, so the ONLY difference is which service object
 * is called. Keeping that choice here means the thunks, the slice and the
 * pages never branch on audience for data access.
 *
 * The audience is resolved per call rather than once at module scope: this
 * module is imported during app bootstrap (via `redux/action`), long before
 * any privileges exist in sessionStorage, so a module-level constant would be
 * captured as `false` for every user and would also survive a switch to
 * another account in the same tab. `hasPrivilege` is a cheap sessionStorage
 * read, so there is nothing to gain from caching it.
 *
 * Printing is NOT represented here: the web portal builds the printable sheet
 * client-side from the detail payload (`utility/printHomework`), so it reuses
 * `fetchHomeworkDetail` and never calls the backend's PDF print endpoint.
 */
const isStudentAudience = (): boolean => hasPrivilege(APP_FEATURES.STUDENT_HOMEWORK);

const fetchHomeworkList = (
  studentId: string,
  params?: IParentHomeworkListQueryParams,
): Promise<AxiosResponse<IParentHomeworkListResponse>> =>
  isStudentAudience()
    ? studentHomeworkService.fetchStudentHomeworkList(studentId, params)
    : parentHomeworkService.fetchParentHomeworkList(studentId, params);

const fetchHomeworkFilters = (
  studentId: string,
): Promise<AxiosResponse<IParentHomeworkFiltersResponse>> =>
  isStudentAudience()
    ? studentHomeworkService.fetchStudentHomeworkFilters(studentId)
    : parentHomeworkService.fetchParentHomeworkFilters(studentId);

const fetchHomeworkDetail = (
  homeworkId: string,
  studentId: string,
): Promise<AxiosResponse<IParentHomeworkDetailResponse>> =>
  isStudentAudience()
    ? studentHomeworkService.fetchStudentHomeworkDetail(homeworkId, studentId)
    : parentHomeworkService.fetchParentHomeworkDetail(homeworkId, studentId);

const submitHomework = (
  homeworkId: string,
  studentId: string,
  filesByTask: ParentSubmissionFilesByTask,
): Promise<AxiosResponse<IParentHomeworkSubmitResponse>> =>
  isStudentAudience()
    ? studentHomeworkService.submitStudentHomework(homeworkId, studentId, filesByTask)
    : parentHomeworkService.submitParentHomework(homeworkId, studentId, filesByTask);

export const homeworkPortalService = {
  fetchHomeworkList,
  fetchHomeworkFilters,
  fetchHomeworkDetail,
  submitHomework,
};
